import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  HiChatBubbleLeftEllipsis,
  HiEye,
  HiMagnifyingGlass,
  HiPlusCircle,
  HiPencilSquare,
  HiTrash,
  HiArrowPath,
  HiShare,
  HiCodeBracket,
  HiCpuChip,
  HiDocumentText,
  HiArrowDownTray,
  HiDocumentChartBar,
} from 'react-icons/hi2';
import { toast } from 'react-toastify';
import {
  deleteSubmission,
  getAllSubmissions,
  addSubmissionFeedback,
  updateSubmissionFeedback,
  deleteSubmissionFeedback,
  exportSubmissionsToCSV,
  exportSubmissionsToJSON,
  getSubmissionAudits,
  CATEGORIES,
  SUBMISSION_STATUS,
} from '../../services/productDevelopmentService';
import { STAGE_NAMES } from './StageSubmissionForm';
import SubmissionPreviewModal from './SubmissionPreviewModal';
import SubmissionEditorModal from './SubmissionEditorModal';
import SubmissionFeedbackModal from './SubmissionFeedbackModal';
import StaffSubmissionFormModal from './StaffSubmissionFormModal';
import ShareModal from './ShareModal';
import AuditReportModal from './AuditReportModal';
import { listAllPeople } from '../../services/userDirectoryService';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const ClientSubmissionsPanel = ({ currentUser, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, submissionId: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [stageFilter, setStageFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [shareSubmission, setShareSubmission] = useState(null);
  const [editorSubmission, setEditorSubmission] = useState(null);
  const [feedbackSubmission, setFeedbackSubmission] = useState(null);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [clients, setClients] = useState([]);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [auditSubmission, setAuditSubmission] = useState(null);
  const [submissionAudits, setSubmissionAudits] = useState([]);
  const [loadingAudits, setLoadingAudits] = useState(false);

  useEffect(() => {
    if (feedbackSubmission) {
      const updated = submissions.find(item => item.id === feedbackSubmission.id);
      if (updated) {
        // Ensure updated submission has all required fields
        const safeSubmission = {
          ...updated,
          feedback: Array.isArray(updated.feedback) ? updated.feedback : [],
          id: updated.id || updated.docId,
          stageName: updated.stageName || STAGE_NAMES[updated.stage] || updated.stage,
          userEmail: updated.userEmail || updated.clientEmail,
        };
        setFeedbackSubmission(safeSubmission);
      }
    }
    if (editorSubmission) {
      const updated = submissions.find(item => item.id === editorSubmission.id);
      if (updated) {
        setEditorSubmission(updated);
      }
    }
    if (selectedSubmission) {
      const updated = submissions.find(item => item.id === selectedSubmission.id);
      if (updated) {
        setSelectedSubmission(updated);
      }
    }
  }, [submissions, feedbackSubmission, editorSubmission, selectedSubmission]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [submissionsData, people] = await Promise.all([
          getAllSubmissions({ userRole: userRole }), // Pass userRole to get all submissions for staff
          listAllPeople({ includeClients: true }),
        ]);

        const clientDirectoryRaw = people.filter(person =>
          person.roles.some(role => role.toLowerCase() === 'client')
        );

        const clientDirectory = clientDirectoryRaw.map(person => ({
          uid: person.uid || person.email,
          email: person.email,
          name: person.name || person.email,
          roles: person.roles,
        }));

        const directoryMap = new Map();
        clientDirectory.forEach(person => {
          const entry = { name: person.name, email: person.email, uid: person.uid };
          directoryMap.set(person.uid, entry);
          if (person.email && person.email !== person.uid) {
            directoryMap.set(person.email, entry);
          }
        });

        const hydrated = submissionsData.map(submission => {
          const directoryEntry =
            directoryMap.get(submission.userId) || directoryMap.get(submission.userEmail);
          return {
            ...submission,
            clientName: directoryEntry?.name || submission.userEmail || 'Unknown client',
            clientEmail: directoryEntry?.email || submission.userEmail || 'N/A',
          };
        });

        setSubmissions(hydrated);
        setClients(clientDirectory);
      } catch (error) {
        toast.error('Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshToken]);

  const filteredSubmissions = useMemo(() => {
    return submissions.filter(submission => {
      const keywords =
        `${submission.stageName} ${submission.clientName} ${submission.clientEmail} ${submission.stage} ${submission.category || ''} ${submission.status || ''} ${submission.title || ''}`.toLowerCase();
      const matchesSearch = keywords.includes(searchTerm.trim().toLowerCase());
      const matchesStage = !stageFilter || submission.stage === stageFilter;
      const matchesCategory = !categoryFilter || submission.category === categoryFilter;
      const matchesStatus = !statusFilter || submission.status === statusFilter;
      return matchesSearch && matchesStage && matchesCategory && matchesStatus;
    });
  }, [submissions, searchTerm, stageFilter, categoryFilter, statusFilter]);

  const formatDate = value => {
    if (!value) return 'N/A';
    if (value.toDate) {
      return value.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDeleteClick = submissionId => {
    setDeleteConfirm({ isOpen: true, submissionId });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.submissionId) return;

    setIsDeleting(true);
    try {
      await deleteSubmission(deleteConfirm.submissionId);
      toast.success('Submission deleted');
      setDeleteConfirm({ isOpen: false, submissionId: null });
      setRefreshToken(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to delete submission');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, submissionId: null });
  };

  const handleFeedbackAction = async (action, submissionId, feedbackId, payload) => {
    setFeedbackSaving(true);
    try {
      if (action === 'create') {
        await addSubmissionFeedback(submissionId, payload);
      } else if (action === 'update') {
        await updateSubmissionFeedback(submissionId, feedbackId, payload);
      } else if (action === 'delete') {
        await deleteSubmissionFeedback(submissionId, feedbackId);
      }
      toast.success('Feedback updated');
      setRefreshToken(prev => prev + 1);
    } catch (error) {
      toast.error('Unable to update feedback');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleExportCSV = () => {
    try {
      const csvContent = exportSubmissionsToCSV(filteredSubmissions);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `product_development_submissions_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportJSON = () => {
    try {
      const jsonContent = exportSubmissionsToJSON(filteredSubmissions);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `product_development_submissions_${new Date().toISOString().split('T')[0]}.json`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('JSON exported successfully');
    } catch (error) {
      toast.error('Failed to export JSON');
    }
  };

  const handleViewAudit = async submission => {
    setAuditSubmission(submission);
    setLoadingAudits(true);
    try {
      const audits = await getSubmissionAudits(submission.id);
      setSubmissionAudits(audits);
    } catch (error) {
      toast.error('Failed to load audit reports');
    } finally {
      setLoadingAudits(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-lg md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-black md:text-3xl">
              Client Submissions Overview
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Review, update, comment, and manage all product development submissions
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setRefreshToken(prev => prev + 1)}
              className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 hover:shadow-md"
            >
              <HiArrowPath className="h-4 w-4" />
              Refresh
            </button>
            <div className="export-menu-container relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 hover:shadow-md"
              >
                <HiArrowDownTray className="h-4 w-4" />
                Export
              </button>
              {showExportMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 top-full z-10 mt-2 w-40 rounded-xl border border-gray-200 bg-white shadow-xl"
                >
                  <button
                    onClick={() => {
                      handleExportCSV();
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors first:rounded-t-xl hover:bg-gray-50"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => {
                      handleExportJSON();
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors last:rounded-b-xl hover:bg-gray-50"
                  >
                    Export as JSON
                  </button>
                </motion.div>
              )}
            </div>
            <button
              onClick={() => setShowStaffForm(true)}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl"
            >
              <HiPlusCircle className="h-4 w-4" />
              Log Submission
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <div className="relative">
            <HiMagnifyingGlass className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Search by client, title, stage, category..."
              className="w-full rounded-xl border border-gray-300 py-3 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <select
              value={categoryFilter}
              onChange={event => setCategoryFilter(event.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">All Categories</option>
              <option value={CATEGORIES.SOFTWARE}>Software</option>
              <option value={CATEGORIES.NON_SOFTWARE}>Non-Software</option>
            </select>
            <select
              value={stageFilter}
              onChange={event => setStageFilter(event.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">All Stages</option>
              {Object.entries(STAGE_NAMES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">All Statuses</option>
              <option value={SUBMISSION_STATUS.SUBMITTED}>Submitted</option>
              <option value={SUBMISSION_STATUS.UNDER_REVIEW}>Under Review</option>
              <option value={SUBMISSION_STATUS.CHANGES_REQUIRED}>Changes Required</option>
              <option value={SUBMISSION_STATUS.APPROVED}>Approved</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
        <div className="border-b border-gray-200 px-4 py-3 md:px-6">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-black">
              {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}
            </span>
            {loading && <span className="text-xs text-gray-500">Loading latest data...</span>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Client / Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Stage
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Submitted
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Feedback
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                      Loading submissions...
                    </div>
                  </td>
                </tr>
              ) : filteredSubmissions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <HiDocumentText className="h-12 w-12 text-gray-300" />
                      <p>No submissions match your filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSubmissions.map(submission => {
                  const status = submission.status || SUBMISSION_STATUS.SUBMITTED;
                  const statusColors = {
                    [SUBMISSION_STATUS.SUBMITTED]: 'bg-gray-100 text-gray-700',
                    [SUBMISSION_STATUS.UNDER_REVIEW]: 'bg-blue-100 text-blue-700',
                    [SUBMISSION_STATUS.CHANGES_REQUIRED]: 'bg-orange-100 text-orange-700',
                    [SUBMISSION_STATUS.APPROVED]: 'bg-green-100 text-green-700',
                  };
                  const categoryIcon =
                    submission.category === CATEGORIES.SOFTWARE ? HiCodeBracket : HiCpuChip;
                  const CategoryIcon = categoryIcon;
                  return (
                    <motion.tr
                      key={submission.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-gray-100 transition-colors hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent"
                    >
                      <td className="px-4 py-4 text-sm">
                        <div className="font-semibold text-black">
                          {submission.title || submission.clientName}
                        </div>
                        <div className="text-xs text-gray-500">{submission.clientEmail}</div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <CategoryIcon
                            className={`h-4 w-4 ${
                              submission.category === CATEGORIES.SOFTWARE
                                ? 'text-blue-600'
                                : 'text-purple-600'
                            }`}
                          />
                          <span className="font-medium capitalize text-gray-700">
                            {submission.category || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {STAGE_NAMES[submission.stage] || submission.stageName || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            statusColors[status] || statusColors[SUBMISSION_STATUS.SUBMITTED]
                          }`}
                        >
                          {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {formatDate(submission.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                          {(submission.feedback && submission.feedback.length) || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-medium">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setSelectedSubmission(submission)}
                            className="rounded-lg p-2 text-blue-600 transition-all hover:bg-blue-50 hover:shadow-sm"
                            title="Preview"
                          >
                            <HiEye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewAudit(submission)}
                            className="rounded-lg p-2 text-purple-600 transition-all hover:bg-purple-50 hover:shadow-sm"
                            title="View Audit Report"
                          >
                            <HiDocumentChartBar className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setShareSubmission(submission)}
                            className="rounded-lg p-2 text-green-600 transition-all hover:bg-green-50 hover:shadow-sm"
                            title="Share"
                          >
                            <HiShare className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              const safeSubmission = {
                                ...submission,
                                feedback: Array.isArray(submission.feedback)
                                  ? submission.feedback
                                  : [],
                                id: submission.id || submission.docId,
                                stageName:
                                  submission.stageName ||
                                  STAGE_NAMES[submission.stage] ||
                                  submission.stage,
                                userEmail: submission.userEmail || submission.clientEmail,
                              };
                              setFeedbackSubmission(safeSubmission);
                            }}
                            className="rounded-lg p-2 text-teal-600 transition-all hover:bg-teal-50 hover:shadow-sm"
                            title="Feedback"
                          >
                            <HiChatBubbleLeftEllipsis className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditorSubmission(submission)}
                            className="rounded-lg p-2 text-gray-600 transition-all hover:bg-gray-100 hover:shadow-sm"
                            title="Edit"
                          >
                            <HiPencilSquare className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(submission.id)}
                            className="rounded-lg p-2 text-red-600 transition-all hover:bg-red-50 hover:shadow-sm"
                            title="Delete"
                          >
                            <HiTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SubmissionPreviewModal
        isOpen={!!selectedSubmission}
        submission={selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
      />

      <SubmissionEditorModal
        isOpen={!!editorSubmission}
        submission={editorSubmission}
        onClose={() => setEditorSubmission(null)}
        onUpdated={() => setRefreshToken(prev => prev + 1)}
      />

      <SubmissionFeedbackModal
        isOpen={!!feedbackSubmission}
        submission={feedbackSubmission}
        onClose={() => setFeedbackSubmission(null)}
        onAddFeedback={(submissionId, payload) =>
          handleFeedbackAction('create', submissionId, null, payload)
        }
        onUpdateFeedback={(submissionId, feedbackId, payload) =>
          handleFeedbackAction('update', submissionId, feedbackId, payload)
        }
        onDeleteFeedback={(submissionId, feedbackId) =>
          handleFeedbackAction('delete', submissionId, feedbackId)
        }
        currentUser={{
          uid: currentUser?.uid,
          email: currentUser?.email,
          name: currentUser?.displayName || currentUser?.name,
          role: userRole,
        }}
        saving={feedbackSaving}
      />

      <StaffSubmissionFormModal
        isOpen={showStaffForm}
        onClose={() => setShowStaffForm(false)}
        onSuccess={() => setRefreshToken(prev => prev + 1)}
        clients={clients}
        currentUser={currentUser}
      />

      <ShareModal
        isOpen={!!shareSubmission}
        onClose={() => setShareSubmission(null)}
        submission={shareSubmission}
      />

      <AuditReportModal
        isOpen={!!auditSubmission}
        onClose={() => {
          setAuditSubmission(null);
          setSubmissionAudits([]);
        }}
        submission={auditSubmission}
        audits={submissionAudits}
        loading={loadingAudits}
      />

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Submission"
        message="Are you sure you want to delete this submission? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ClientSubmissionsPanel;
